<template>
  <div
    :id="uuid"
    class=""
    :style="{
      'scroll-margin-top': pageEditorStore.scrollMarginTop,
    }"
  >
    <BannersContent
      v-if="!hasResponsiveOverrides"
      :banners="formData.banners"
      :block-shadow="formData.blockShadow"
    />
    <template v-else>
      <div class="sm:hidden">
        <BannersContent
          :banners="mobileView"
          :block-shadow="formData.blockShadow"
        />
      </div>
      <div class="hidden sm:block lg:hidden">
        <BannersContent
          :banners="mediumView"
          :block-shadow="formData.blockShadow"
        />
      </div>
      <div class="hidden lg:block">
        <BannersContent
          :banners="desktopView"
          :block-shadow="formData.blockShadow"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import type {
  BannerDisplayData,
  BannerFormData,
  BannersFormData,
  BannerTextAlign,
} from './types'
import BannersContent from './BannersContent.vue'
import { usePageEditor } from '~/stores/page-editor'

const pageEditorStore = usePageEditor()

interface ResponsiveSettings {
  isEnabled: boolean
  hasBgImage: boolean
  bgImage?: string
  hasBgColor: boolean
  bgColor?: string
  textAlign?: BannerTextAlign
}

interface PropsInterface {
  uuid: string
  formData: BannersFormData
}

const props = defineProps<PropsInterface>()

const desktopView = computed<BannerDisplayData[]>(() => props.formData.banners)

const hasResponsiveOverrides = computed(() =>
  props.formData.banners.some(
    (banner) =>
      banner.overrideMobileView === true || banner.overrideMediumView === true
  )
)

const hasOwnProperty = (
  banner: BannerFormData,
  property: keyof BannerFormData
) => Object.prototype.hasOwnProperty.call(banner, property)

const resolveResponsiveBanner = (
  banner: BannerFormData,
  settings: ResponsiveSettings
): BannerDisplayData => {
  if (!settings.isEnabled) {
    return banner
  }

  let isBgImage = banner.isBgImage
  let bgImage = banner.bgImage
  let bgColor = banner.bgColor

  if (settings.hasBgImage) {
    bgImage = settings.bgImage || undefined
    bgColor = settings.bgColor ?? bgColor
    isBgImage = Boolean(bgImage)
  } else if (settings.hasBgColor) {
    isBgImage = false
    bgImage = undefined
    bgColor = settings.bgColor
  }

  return {
    ...banner,
    isBgImage,
    bgImage,
    bgColor,
    textAlign: settings.textAlign ?? banner.textAlign,
  }
}

const mobileView = computed<BannerDisplayData[]>(() =>
  props.formData.banners.map((banner) =>
    resolveResponsiveBanner(banner, {
      isEnabled: banner.overrideMobileView === true,
      hasBgImage: hasOwnProperty(banner, 'mobileBgImage'),
      bgImage: banner.mobileBgImage,
      hasBgColor: hasOwnProperty(banner, 'mobileBgColor'),
      bgColor: banner.mobileBgColor,
      textAlign: banner.mobileTextAlign,
    })
  )
)

const mediumView = computed<BannerDisplayData[]>(() =>
  props.formData.banners.map((banner) =>
    resolveResponsiveBanner(banner, {
      isEnabled: banner.overrideMediumView === true,
      hasBgImage: hasOwnProperty(banner, 'mediumBgImage'),
      bgImage: banner.mediumBgImage,
      hasBgColor: hasOwnProperty(banner, 'mediumBgColor'),
      bgColor: banner.mediumBgColor,
      textAlign: banner.mediumTextAlign,
    })
  )
)
</script>
